import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import {
  ClockSettings,
  ClockContextValue,
  ClockFormat,
  ClockFaceStyle,
  GradientPreset,
  TextureOverlay,
  TimeZoneInfo,
  DigitalFontStyle,
  FontSize,
  Timer,
  Alarm,
  AlarmRepeat
} from '@/types/clock';
import {
  searchCities as searchCitiesUtil,
  getTimeInZone,
  getUTCOffset,
  getLocalTimezone,
  getLocalCityName
} from '@/utils/timezones';

const ClockContext = createContext<ClockContextValue | undefined>(undefined);

export const useClock = () => {
  const context = useContext(ClockContext);
  if (!context) {
    throw new Error('useClock must be used within a ClockProvider');
  }
  return context;
};

export const ClockProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTime, setCurrentTime] = useState(new Date());
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState<ClockSettings>(() => {
    // Default settings
    const localTz = getLocalTimezone();
    const localCity = getLocalCityName();
    
    return {
      format: '12h',
      showSeconds: true,
      showDate: true,
      showTimeZone: true,
      clockFaceStyle: 'analog-luxury',
      digitalFontStyle: 'inter',
      fontSize: 'medium',
      gradientPreset: 'ocean',
      textureOverlay: 'none',
      theme: 'dark',
      timeZones: [
        {
          id: 'local',
          city: localCity,
          timezone: localTz,
          offset: getUTCOffset(localTz),
          time: new Date()
        }
      ],
      timers: [],
      alarms: []
    };
  });

  // Helper to get org-specific localStorage key
  const getOrgKey = useCallback((key: string): string => {
    const orgId = localStorage.getItem('current_organization_id');
    return orgId ? `org_${orgId}_clock_${key}` : `clock_${key}`;
  }, []);

  // Load settings from localStorage
  const loadSettings = useCallback(() => {
    try {
      const savedSettings = localStorage.getItem(getOrgKey('settings'));
      if (savedSettings) {
        const parsed = JSON.parse(savedSettings);
        
        // Reconstruct Date objects for timeZones
        if (parsed.timeZones) {
          parsed.timeZones = parsed.timeZones.map((tz: any) => ({
            ...tz,
            time: new Date(tz.time)
          }));
        }
        
        // Ensure timers and alarms arrays exist (backwards compatibility)
        if (!parsed.timers) {
          parsed.timers = [];
        }
        if (!parsed.alarms) {
          parsed.alarms = [];
        }
        
        setSettings(parsed);
        console.log('[Clock] Loaded settings:', parsed);
      }
    } catch (error) {
      console.error('[Clock] Failed to load settings:', error);
    } finally {
      setLoading(false);
    }
  }, [getOrgKey]);

  // Save settings to localStorage
  const saveSettings = useCallback((newSettings: ClockSettings) => {
    try {
      localStorage.setItem(getOrgKey('settings'), JSON.stringify(newSettings));
      console.log('[Clock] Saved settings:', newSettings);
    } catch (error) {
      console.error('[Clock] Failed to save settings:', error);
    }
  }, [getOrgKey]);

  // Load settings on mount and org changes
  useEffect(() => {
    const checkOrgChange = () => {
      const currentOrgId = localStorage.getItem('current_organization_id');
      const savedOrgId = sessionStorage.getItem('clock_current_org');
      
      if (currentOrgId !== savedOrgId) {
        sessionStorage.setItem('clock_current_org', currentOrgId || '');
        loadSettings();
      }
    };

    loadSettings();
    const interval = setInterval(checkOrgChange, 1000);
    
    return () => clearInterval(interval);
  }, [loadSettings]);

  // Update time every second - ONLY update currentTime, not settings
  useEffect(() => {
    const updateTime = () => {
      setCurrentTime(new Date());
    };

    updateTime(); // Initial update
    const interval = setInterval(updateTime, 1000);
    
    return () => clearInterval(interval);
  }, []);

  // Add time zone
  const addTimeZone = useCallback((city: string, timezone: string) => {
    setSettings(prev => {
      // Check if timezone already exists
      if (prev.timeZones.some(tz => tz.timezone === timezone)) {
        console.warn('[Clock] Timezone already added:', timezone);
        return prev;
      }

      const newTimeZone: TimeZoneInfo = {
        id: `${timezone}-${Date.now()}`,
        city,
        timezone,
        offset: getUTCOffset(timezone),
        time: getTimeInZone(timezone)
      };

      const newSettings = {
        ...prev,
        timeZones: [...prev.timeZones, newTimeZone]
      };

      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Remove time zone
  const removeTimeZone = useCallback((id: string) => {
    setSettings(prev => {
      // Don't allow removing the last time zone
      if (prev.timeZones.length <= 1) {
        console.warn('[Clock] Cannot remove the last timezone');
        return prev;
      }

      const newSettings = {
        ...prev,
        timeZones: prev.timeZones.filter(tz => tz.id !== id)
      };

      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update format
  const updateFormat = useCallback((format: ClockFormat) => {
    setSettings(prev => {
      const newSettings = { ...prev, format };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update clock face style
  const updateClockFace = useCallback((style: ClockFaceStyle) => {
    setSettings(prev => {
      const newSettings = { ...prev, clockFaceStyle: style };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update digital font style
  const updateDigitalFont = useCallback((font: DigitalFontStyle) => {
    setSettings(prev => {
      const newSettings = { ...prev, digitalFontStyle: font };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update font size
  const updateFontSize = useCallback((size: FontSize) => {
    setSettings(prev => {
      const newSettings = { ...prev, fontSize: size };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update gradient
  const updateGradient = useCallback((preset: GradientPreset) => {
    setSettings(prev => {
      const newSettings = { ...prev, gradientPreset: preset };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update texture
  const updateTexture = useCallback((texture: TextureOverlay) => {
    setSettings(prev => {
      const newSettings = { ...prev, textureOverlay: texture };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Toggle seconds
  const toggleSeconds = useCallback(() => {
    setSettings(prev => {
      const newSettings = { ...prev, showSeconds: !prev.showSeconds };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Toggle date
  const toggleDate = useCallback(() => {
    setSettings(prev => {
      const newSettings = { ...prev, showDate: !prev.showDate };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Toggle timezone display
  const toggleTimeZone = useCallback(() => {
    setSettings(prev => {
      const newSettings = { ...prev, showTimeZone: !prev.showTimeZone };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update theme
  const updateTheme = useCallback((theme: 'light' | 'dark') => {
    setSettings(prev => {
      const newSettings = { ...prev, theme };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Update multiple settings at once
  const updateSettings = useCallback((partial: Partial<ClockSettings>) => {
    setSettings(prev => {
      const newSettings = { ...prev, ...partial };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // Search cities
  const searchCities = useCallback((query: string) => {
    return searchCitiesUtil(query);
  }, []);

  // === TIMER FUNCTIONS ===
  
  const addTimer = useCallback((durationMs: number, label?: string): string => {
    const id = `timer-${Date.now()}`;
    const timer: Timer = {
      id,
      label,
      durationMs,
      targetTs: Date.now() + durationMs,
      running: true,
      createdAt: Date.now()
    };
    
    setSettings(prev => {
      // Defensive check: ensure timers array exists
      const existingTimers = prev.timers || [];
      const newSettings = { ...prev, timers: [...existingTimers, timer] };
      saveSettings(newSettings);
      return newSettings;
    });
    
    return id;
  }, [saveSettings]);
  
  const pauseTimer = useCallback((id: string) => {
    setSettings(prev => {
      const existingTimers = prev.timers || [];
      const timer = existingTimers.find(t => t.id === id);
      if (!timer || !timer.running) return prev;
      
      const remaining = timer.targetTs - Date.now();
      const newSettings = {
        ...prev,
        timers: existingTimers.map(t => 
          t.id === id ? { ...t, running: false, pausedAt: remaining } : t
        )
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);
  
  const resumeTimer = useCallback((id: string) => {
    setSettings(prev => {
      const existingTimers = prev.timers || [];
      const timer = existingTimers.find(t => t.id === id);
      if (!timer || timer.running || !timer.pausedAt) return prev;
      
      const newSettings = {
        ...prev,
        timers: existingTimers.map(t =>
          t.id === id ? { ...t, running: true, targetTs: Date.now() + t.pausedAt!, pausedAt: undefined } : t
        )
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);
  
  const cancelTimer = useCallback((id: string) => {
    setSettings(prev => {
      const existingTimers = prev.timers || [];
      const newSettings = {
        ...prev,
        timers: existingTimers.filter(t => t.id !== id)
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);

  // === ALARM FUNCTIONS ===
  
  const computeNextTrigger = (time: { hour: number; minute: number }, repeat: AlarmRepeat): number => {
    const now = new Date();
    const alarm = new Date();
    alarm.setHours(time.hour, time.minute, 0, 0);
    
    // If alarm time is in the past today, move to tomorrow or next occurrence
    if (alarm.getTime() <= now.getTime()) {
      if (repeat === 'once') {
        alarm.setDate(alarm.getDate() + 1);
      } else if (repeat === 'daily') {
        alarm.setDate(alarm.getDate() + 1);
      } else if (repeat === 'weekdays') {
        // Move to next weekday
        do {
          alarm.setDate(alarm.getDate() + 1);
        } while (alarm.getDay() === 0 || alarm.getDay() === 6); // Skip Sunday(0) and Saturday(6)
      } else if (repeat === 'weekends') {
        // Move to next weekend day
        do {
          alarm.setDate(alarm.getDate() + 1);
        } while (alarm.getDay() !== 0 && alarm.getDay() !== 6);
      }
    }
    
    return alarm.getTime();
  };
  
  const addAlarm = useCallback((time: { hour: number; minute: number }, repeat: AlarmRepeat, label?: string): string => {
    const id = `alarm-${Date.now()}`;
    const alarm: Alarm = {
      id,
      label,
      time,
      repeat,
      enabled: true,
      nextTriggerTs: computeNextTrigger(time, repeat),
      createdAt: Date.now()
    };
    
    setSettings(prev => {
      const existingAlarms = prev.alarms || [];
      const newSettings = { ...prev, alarms: [...existingAlarms, alarm] };
      saveSettings(newSettings);
      return newSettings;
    });
    
    return id;
  }, [saveSettings]);
  
  const toggleAlarm = useCallback((id: string, enabled: boolean) => {
    setSettings(prev => {
      const existingAlarms = prev.alarms || [];
      const newSettings = {
        ...prev,
        alarms: existingAlarms.map(a =>
          a.id === id ? { ...a, enabled } : a
        )
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);
  
  const deleteAlarm = useCallback((id: string) => {
    setSettings(prev => {
      const existingAlarms = prev.alarms || [];
      const newSettings = {
        ...prev,
        alarms: existingAlarms.filter(a => a.id !== id)
      };
      saveSettings(newSettings);
      return newSettings;
    });
  }, [saveSettings]);
  
  const snoozeAlarm = useCallback((label?: string) => {
    // Create a temporary one-time alarm 5 minutes from now
    const now = new Date();
    const snoozeTime = new Date(now.getTime() + 5 * 60 * 1000); // 5 minutes
    
    const snoozeAlarmData: Alarm = {
      id: `alarm-snooze-${Date.now()}`,
      label: label ? `${label} (Snoozed)` : 'Snoozed Alarm',
      time: {
        hour: snoozeTime.getHours(),
        minute: snoozeTime.getMinutes()
      },
      repeat: 'once',
      enabled: true,
      nextTriggerTs: snoozeTime.getTime(),
      createdAt: Date.now()
    };
    
    setSettings(prev => {
      const existingAlarms = prev.alarms || [];
      const newSettings = { ...prev, alarms: [...existingAlarms, snoozeAlarmData] };
      saveSettings(newSettings);
      return newSettings;
    });
    
    console.log('[Clock] Snoozed alarm for 5 minutes:', snoozeAlarmData);
  }, [saveSettings]);

  const value: ClockContextValue = {
    settings,
    currentTime,
    loading,
    addTimeZone,
    removeTimeZone,
    updateFormat,
    updateClockFace,
    updateDigitalFont,
    updateFontSize,
    updateGradient,
    updateTexture,
    toggleSeconds,
    toggleDate,
    toggleTimeZone,
    updateTheme,
    updateSettings,
    searchCities,
    // Timers
    addTimer,
    pauseTimer,
    resumeTimer,
    cancelTimer,
    // Alarms
    addAlarm,
    toggleAlarm,
    deleteAlarm,
    snoozeAlarm
  };

  return (
    <ClockContext.Provider value={value}>
      {children}
    </ClockContext.Provider>
  );
};
